import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { SystemInlineBanner, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";
import { useRef } from "react";

import { ListLoadingState } from "../../../../../components/ListLoadingState";
import { PageWrapper } from "../../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../../components/StickyScrollPageHeader";
import { PageContent } from "../../../../../WorkerShifts/components/PageContent";
import { DocumentsErrorPage } from "../../../../ErrorPage";
import { DocumentDetailsMetadata } from "../../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../../RequiredBySection/Section";
import { useRequirementDetailsContext } from "../../../useRequirementDetailsContext";
import { useRequirementPrerequisiteActions } from "../../../useRequirementPrerequisiteActions";
import { useGetProfessionalReferences } from "../api/useGetProfessionalReferences";
import {
  DEFAULT_PAGE_TITLE,
  PROFESSIONAL_REFERENCE_STATUS,
  REFERENCE_STATUS_SORT_ORDER,
} from "../constants";
import { AddReferenceDialog } from "./AddReferenceDialog";
import { ReferenceCard } from "./ReferenceCard";

export function ProfessionalReferencesWorkflowView() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const title = workerRequirement?.requirement?.attributes?.name ?? DEFAULT_PAGE_TITLE;

  const { data, isLoading, isError } = useGetProfessionalReferences();
  const addReferenceModalState = useModalState();
  const { withPrerequisiteCheck, prerequisiteBottomSheet } = useRequirementPrerequisiteActions();

  useLogEffect(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCES_PAGE_VIEWED);

  if (isLoading) {
    return (
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          shouldWrapTitle
          title={title}
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
        />
        <ListLoadingState texts={["Loading references..."]} />
      </PageWrapper>
    );
  }

  if (isError) {
    return <DocumentsErrorPage title="Error loading references" />;
  }

  const referencesCount = data?.references.length ?? 0;

  const sortedReferences = isDefined(data?.references)
    ? [...data.references].sort((a, b) => {
        return (
          (REFERENCE_STATUS_SORT_ORDER[a.status] ?? 0) -
          (REFERENCE_STATUS_SORT_ORDER[b.status] ?? 0)
        );
      })
    : undefined;

  const completedReferences = data?.references?.filter((reference) =>
    [PROFESSIONAL_REFERENCE_STATUS.COMPLETED, PROFESSIONAL_REFERENCE_STATUS.VERIFIED].some(
      (status) => reference.status === status
    )
  );

  const needsMoreReferences = isDefined(data) && !data.completed;

  const required = data?.minVerifiedReferenceRequired ?? 0;
  const remaining = required - (completedReferences?.length ?? 0);

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={title}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />
      <PageContent ref={scrollRef}>
        <Stack gap={6}>
          <Text variant="body1">
            Add your professional references below. Choose a person you have worked with directly,
            like a former supervisor, colleagues, or clients.
          </Text>

          <DocumentDetailsMetadata />

          {isDefined(data) &&
            (needsMoreReferences ? (
              <SystemInlineBanner color="warning" iconType="triangle-exclamation">
                Add {remaining} {pluralize("reference", remaining)} to complete
              </SystemInlineBanner>
            ) : (
              <SystemInlineBanner color="success" iconType="checkmark-filled">
                You're all set 🎉
              </SystemInlineBanner>
            ))}

          {isDefined(data) && isDefined(sortedReferences) && sortedReferences.length > 0 && (
            <Stack gap={6}>
              {sortedReferences.map((reference) => (
                <ReferenceCard
                  key={reference._id}
                  reference={reference}
                  completed={data.completed}
                />
              ))}
            </Stack>
          )}
          <DocumentDetailsRequiredBySection />
        </Stack>
      </PageContent>

      {needsMoreReferences && (
        <PageFooterWrapper>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={withPrerequisiteCheck(addReferenceModalState.openModal)}
          >
            Add Reference
          </Button>
        </PageFooterWrapper>
      )}

      <AddReferenceDialog modalState={addReferenceModalState} count={referencesCount + 1} />
      {prerequisiteBottomSheet}
    </PageWrapper>
  );
}
